import { useCallback, useEffect, useMemo, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
// import BusinessIcon from "@mui/icons-material/Business";
import {
  Box,
  Grid,
  TextField as MUITextField,
  Typography,
} from "@mui/material";
import MuiAutocomplete from "@mui/material/Autocomplete";
import match from "autosuggest-highlight/match";
import parse from "autosuggest-highlight/parse";
import { AxiosResponse } from "axios";
import {
  AutocompleteProps,
  fieldToAutocomplete,
  fieldToTextField,
} from "formik-mui";
import { throttle } from "lodash";
import queryString from "query-string";

import { axios } from "../api";
import { useHasPermission } from "../hooks/useHasPermission";
import { FormikAddAccount } from "./FormikAddAccount";

import type { AeronetV6ItemsResponse } from "../types";
import type { AccountType, CompanyV6 } from "./types";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { useGlobalConfig } from "../hooks/useGlobalConfig";

const getCompanyLabel = (
  company: CompanyV6,
  showCurrency = true
): string =>
  showCurrency && company.currency
    ? `${company.name} (${company.currency})`
    : company.name || "";

const isTruthyConfigValue = (value: unknown, defaultValue = true): boolean => {
  if (value === undefined || value === null || value === "") {
    return defaultValue;
  }
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "number") {
    return value === 1;
  }
  const normalized = String(value).toLowerCase();
  return normalized === "1" || normalized === "true";
};

export interface CompanyAutocompleteProps<
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined
> extends AutocompleteProps<T, Multiple, DisableClearable, FreeSolo> {
  companyType?: AccountType;
  label?: string;
  disableFilterName?: boolean;
  notHasAddNewCompanyPermission?: boolean;
  disable?:boolean;
  /** When false, omit "(CURRENCY)" from option labels and the selected value. Default true. */
  showCurrency?: boolean;
}

export function CompanyAutocomplete<
  // eslint-disable-next-line
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined
>(
  props: CompanyAutocompleteProps<
    CompanyV6,
    Multiple,
    DisableClearable,
    FreeSolo
  >
) {
  let {
    label,
    companyType = "",
    disableFilterName = false,
    notHasAddNewCompanyPermission,
    disable,
    showCurrency = true,
    ...autocompleteProps
  } = props;
  if (!label) {
    label = companyType;
  }
  const [companies, setCompanies] = useState<Array<string | CompanyV6>>([]);
  const currentUser = useCurrentUser();
  const globalConfig = useGlobalConfig();
  const [inputValue, setInputValue] = useState("");
  const { name } = fieldToTextField(props as any);
  const { setFieldValue } = autocompleteProps.form;
  // companies_edit — only show Add customer/supplier/company when user has edit permission
  const hasEditPermission = useHasPermission("companies", "edit") === true;
  let addLabel = companyType !== "" ? `Add ${companyType}` : "Add company";
  const allowAddCustomer = isTruthyConfigValue(globalConfig?.add_customer, true);
  const showAddNewOption =
    hasEditPermission &&
    !notHasAddNewCompanyPermission &&
    (companyType !== "customer" || allowAddCustomer);
  const formatCompanyLabel = useCallback(
    (company: CompanyV6) => getCompanyLabel(company, showCurrency),
    [showCurrency]
  );

  const handleSetCompanyValue = useCallback(
    (newAccount: CompanyV6 | "") => {
      setFieldValue(name!, newAccount);
      if (newAccount && typeof newAccount === "object") {
        const displayLabel = formatCompanyLabel(newAccount);
        setInputValue(displayLabel);
        setCompanies((prev) => {
          const companyOptions = prev.filter(
            (item): item is CompanyV6 => typeof item !== "string"
          );
          const exists = companyOptions.some((item) => item.id === newAccount.id);
          const nextCompanies = exists
            ? companyOptions
            : [newAccount, ...companyOptions];
          return showAddNewOption ? [addLabel, ...nextCompanies] : nextCompanies;
        });
        if (props.onChange) {
          props.onChange(
            {} as React.SyntheticEvent,
            newAccount as Parameters<NonNullable<typeof props.onChange>>[1],
            "selectOption"
          );
        }
      } else {
        setInputValue("");
      }
    },
    [addLabel, formatCompanyLabel, name, props, setFieldValue, showAddNewOption]
  );

  const fetchCompanies = useMemo(
    () =>
      throttle((value: string, callback: Function) => {
        let params: { [key: string]: any } = {
          page: 1,
          size: 20,
          sort: "-id",
          filter: [],
        };

        params.filter.push(
          JSON.stringify({
            field: "active",
            op: "==",
            value: true,
          })
        );

        if (companyType !== "") {
          params["filter"].push(
            JSON.stringify({
              field: "type",
              op: "ilike",
              value: "%" + companyType + "%",
            })
          );
        }

        if (!disableFilterName) {
          if (value) {
            params["filter"].push(
              JSON.stringify({
                model: "Company",
                field: "name",
                op: "ilike",
                value: "%" + value + "%",
              })
            );
            params.sort = "name";
          } else {
            params["filter"].push(
              JSON.stringify({
                model: "Company",
                field: "name",
                op: "!=",
                value: "",
              })
            );
          }
        }

        if (notHasAddNewCompanyPermission) {
          params["filter"].push(
            JSON.stringify({
              field: "user_company",
              op: "==",
              value: currentUser?.id || "",
            })
          );
        }
        axios
          .get("/v6/companies", {
            params: params,
            paramsSerializer: (params) => queryString.stringify(params),
          })
          .then((resp: AxiosResponse<AeronetV6ItemsResponse<CompanyV6>>) => {
            callback(resp.data.items);
          })
          .catch(() => {
            callback([]);
          });
      }, 200),
    [companyType, notHasAddNewCompanyPermission, disableFilterName, currentUser]
  );

  useEffect(() => {
    let active = true;

    fetchCompanies(inputValue, (results: ReadonlyArray<CompanyV6>) => {
      if (active) {
        let newCompanies: Array<string | CompanyV6> = [];

        if (results) {
          newCompanies = [...newCompanies, ...results];
        }

        // If we have a current value that's not in the results, add it to ensure it's available
        const currentValue = autocompleteProps.field.value;
        if (currentValue && typeof currentValue === 'object' && currentValue.id) {
          const existsInResults = newCompanies.find(company => 
            typeof company !== 'string' && company.id === currentValue.id
          );
          if (!existsInResults) {
            newCompanies.push(currentValue);
          }
        }

        if (showAddNewOption) {
          newCompanies.unshift(addLabel);
        }

        setCompanies(newCompanies);
      }
    });

    return () => {
      active = false;
    };
  }, [
    inputValue,
    fetchCompanies,
    addLabel,
    showAddNewOption,
    autocompleteProps.field.value,
  ]);

  useEffect(() => {
    const currentValue = autocompleteProps.field.value;
    if (currentValue && typeof currentValue === "object" && currentValue.id) {
      setInputValue(formatCompanyLabel(currentValue));
    } else if (
      typeof currentValue === "string" &&
      currentValue.startsWith("Add")
    ) {
      setInputValue(currentValue);
    } else if (!currentValue) {
      setInputValue("");
    }
  }, [autocompleteProps.field.value, formatCompanyLabel]);

  return (
    <>
      <MuiAutocomplete
        {...fieldToAutocomplete(autocompleteProps)}
        fullWidth
        options={companies as CompanyV6[]}
        autoComplete
        includeInputInList
        disabled={disable || false}
        open = {disable ===true ? false: undefined}
        inputValue={inputValue}
        onChange={(_event: React.SyntheticEvent, value, _reason, _details?) => {
          if (value) {
            setFieldValue(name!, value);
            if (typeof value === "object" && value !== null && !Array.isArray(value)) {
              setInputValue(formatCompanyLabel(value));
            } else if (typeof value === "string") {
              setInputValue(value);
            }
          } else {
            setFieldValue(name!, "");
            setInputValue("");
          }
          if (props.onChange) {
            props.onChange(_event, value, _reason, _details);
          }
        }}
        onInputChange={(_event, newInputValue, reason) => {
          if (reason === "reset") {
            const currentValue = autocompleteProps.field.value;
            if (currentValue && typeof currentValue === "object") {
              setInputValue(formatCompanyLabel(currentValue));
              return;
            }
          }
          setInputValue(newInputValue);
        }}
        getOptionLabel={(option) => {
          if (typeof option === "string") {
            return option;
          }
          return formatCompanyLabel(option);
        }}
        isOptionEqualToValue={(option, value) => {
          // Handle string options (like "Add company")
          if (typeof option === "string" || typeof value === "string") {
            return option === value;
          }
          
          // Handle null/undefined values
          if (!option || !value) {
            return option === value;
          }
          
          // Compare by ID for company objects
          return option.id === value.id;
        }}
        renderInput={(params) => (
          <MUITextField
            {...params}
            label={label}
            error={props.form.touched[name!] && !!props.form.errors[name!]}
            // @ts-ignore
            helperText={props.form.touched[name!] && props.form.errors[name!]}
            sx={{ textTransform: "capitalize" }}
          />
        )}
        renderOption={(props, option: CompanyV6, state) => {
          if (typeof option === "string") {
            return (
              <li {...props} key={-1}>
                <Grid container alignItems="center">
                  <Grid item>
                    <AddIcon
                      sx={{
                        color: "text.secondary",
                        marginRight: 2,
                      }}
                    />
                  </Grid>
                  <Grid item xs>
                    <span
                      style={{
                        fontWeight: 700,
                      }}
                    >
                      {option}
                    </span>
                  </Grid>
                </Grid>
              </li>
            );
          } else {
            const matches = match(option.name, state.inputValue, {
              insideWords: true,
            });
            const companies = parse(option.name, matches);

            return (
              <li {...props} key={option.id}>
                <Grid container alignItems="center">
                  
                  <Grid item xs>
                    {companies.map((company, index) => (
                      <span
                        key={index}
                        style={{
                          fontWeight: company.highlight ? 700 : 400,
                        }}
                      >
                        {company.text}
                      </span>
                    ))}
                    {showCurrency && option.currency && (
                      <Box component="span" sx={{ pl: 0.5 }}>
                        ({option.currency})
                      </Box>
                    )}

                    <Typography variant="body2" color="textSecondary">
                      {option.id}
                    </Typography>
                  </Grid>
                </Grid>
              </li>
            );
          }
        }}
      />
      {showAddNewOption &&
        !disable &&
        typeof props.form.values[name!] === "string" &&
        props.form.values[name!].startsWith("Add") && (
          <FormikAddAccount
            setValue={handleSetCompanyValue}
            // @ts-ignore
            accountType={companyType || "supplier"}
          />
        )}
    </>
  );
}

CompanyAutocomplete.displayName = "FormikMaterialUCompanyV6Autocomplete";
